import { Liquid } from 'liquidjs';
import { generateIntakeFormPrompt } from './intake-form-builder/generate-prompt';
import { STRADA_CONTACT_TIMEZONE, STRADA_CURRENT_TIME } from './constants';
export const OUTCOME_HANDLE_ID_PREFIX = 'outcome_';
const generateSuggestedRepliesXml = (chatSuggestions) => {
    if (!chatSuggestions?.options || chatSuggestions.options.length === 0) {
        return '';
    }
    const options = chatSuggestions.options.filter((opt) => opt.trim());
    if (options.length === 0) {
        return '';
    }
    const isStrict = chatSuggestions.strict;
    const tagName = isStrict ? 'options_for_user_to_choose_strict' : 'options_for_user_to_choose';
    let result = `<${tagName}>\n`;
    options.forEach((option) => {
        const value = option
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '_')
            .replace(/^_+|_+$/g, '');
        result += `  <option value="${value}">${option}</option>\n`;
    });
    result += `</${tagName}>`;
    return result;
};
const generateSuggestedRepliesPrompt = (chatSuggestions, conversationType) => {
    if (!(conversationType === 'chat' || conversationType === 'all')) {
        return '';
    }
    const xml = generateSuggestedRepliesXml(chatSuggestions);
    if (!xml) {
        return '';
    }
    let result = '';
    if (conversationType === 'all') {
        result += `[Chat Suggestions]:\n`;
    }
    result += `\nAfter following the instructions above, say this exactly without adding anything before or after it or changing the formatting at all:\n\n${xml}\n`;
    return result;
};
export const conversationFlowPrompt = (nodes, edges, conversationType, useNewPromptGenerator, intakeFormBuilder) => {
    // Check if this agent should use the new prompt generator
    if (!useNewPromptGenerator) {
        return conversationFlowPrompt_deprecated(nodes, edges, conversationType, intakeFormBuilder);
    }
    // Step 1: Create outcome-to-target-node mapping
    const createOutcomeMapping = () => {
        const outcomeMapping = new Map();
        edges.forEach((edge) => {
            const sourceHandleIndex = parseInt(edge.sourceHandle?.replace(OUTCOME_HANDLE_ID_PREFIX, '') || '-1');
            if (sourceHandleIndex !== -1) {
                if (!outcomeMapping.has(edge.source)) {
                    outcomeMapping.set(edge.source, new Map());
                }
                outcomeMapping.get(edge.source).set(sourceHandleIndex, edge.target);
            }
        });
        return outcomeMapping;
    };
    // Step 2: Assign hierarchical names to all nodes using DFS
    const assignNodeNames = () => {
        const nodeNames = new Map();
        const visited = new Set();
        const dfs = (nodeId, graphNumber) => {
            if (visited.has(nodeId))
                return;
            visited.add(nodeId);
            nodeNames.set(nodeId, graphNumber);
            const outgoingEdges = edges.filter((edge) => edge.source === nodeId);
            outgoingEdges.forEach((edge) => {
                const sourceOutcomeIndex = parseInt(edge.sourceHandle?.replace(OUTCOME_HANDLE_ID_PREFIX, '') || '-1');
                if (sourceOutcomeIndex !== -1) {
                    const childNumber = `${graphNumber}.${sourceOutcomeIndex + 1}`;
                    dfs(edge.target, childNumber);
                }
            });
        };
        // Start from root nodes
        const rootNodes = nodes.filter((node) => edges.every((edge) => edge.target !== node.id));
        rootNodes.forEach((root, index) => {
            dfs(root.id, `${index + 1}`);
        });
        return nodeNames;
    };
    // Step 2.5: Collect all parent outcomes for each node
    const collectParentOutcomes = () => {
        const nodeToParentOutcomes = new Map();
        // Root nodes use their label as the outcome
        const rootNodes = nodes.filter((node) => edges.every((edge) => edge.target !== node.id));
        rootNodes.forEach((rootNode) => {
            nodeToParentOutcomes.set(rootNode.id, [rootNode.data.label]);
        });
        // For all other nodes, collect outcomes from all incoming edges
        edges.forEach((edge) => {
            const sourceNode = nodes.find((n) => n.id === edge.source);
            if (!sourceNode)
                return;
            const sourceOutcomeIndex = parseInt(edge.sourceHandle?.replace(OUTCOME_HANDLE_ID_PREFIX, '') || '-1');
            if (sourceOutcomeIndex === -1)
                return;
            const outcome = sourceNode.data.outcomes[sourceOutcomeIndex] || '';
            if (!nodeToParentOutcomes.has(edge.target)) {
                nodeToParentOutcomes.set(edge.target, []);
            }
            nodeToParentOutcomes.get(edge.target).push(outcome);
        });
        return nodeToParentOutcomes;
    };
    // Step 3: Build conversation prompt using node names instead of numbers
    const generateContent = (nodeNames, outcomeMapping, parentOutcomes, intakeFormConfig) => {
        const visited = new Set();
        const buildNodeContent = (nodeId) => {
            if (visited.has(nodeId)) {
                return ''; // Avoid cycles
            }
            visited.add(nodeId);
            const node = nodes.find((n) => n.id === nodeId);
            if (!node) {
                return ''; // Skip if node not found
            }
            const nodeName = nodeNames.get(nodeId) || '1';
            const { instructions, script, notes, outcomes } = node.data;
            // Get all parent outcomes, deduplicate, and join them with " / "
            const nodeParentOutcomes = parentOutcomes.get(nodeId) || [node.data.label];
            const uniqueOutcomes = [...new Set(nodeParentOutcomes)];
            const parentOutcomeText = uniqueOutcomes.join(' / ');
            const sectionTitle = `(${nodeName}) ${parentOutcomeText}`;
            // In MD, tabs are 2 spaces
            const TAB = '  ';
            // Generate the text for this node
            let flowText = `### ${sectionTitle}\n`;
            // Instructions section
            // conversationType = all is only for book keeping purposes, will never be used in live agents
            if (instructions) {
                flowText += `**Instructions:**${TAB}
${instructions}\n`;
            }
            // Inject intake form block instructions for intake nodes
            if (node.type === 'intakeNode' && intakeFormConfig?.blocks?.length) {
                const intakePrompt = generateIntakeFormPrompt(intakeFormConfig);
                flowText += `\n${intakePrompt}\n`;
            }
            if (node.data.extraChatInstructions) {
                if (conversationType === 'chat') {
                    flowText += `${node.data.extraChatInstructions}\n`;
                }
                else if (conversationType === 'all') {
                    flowText += `[Advanced rules for chat]: ${node.data.extraChatInstructions}\n\n`;
                }
            }
            if (node.data.extraCallInstructions) {
                if (conversationType === 'call') {
                    flowText += `${node.data.extraCallInstructions}\n`;
                }
                else if (conversationType === 'all') {
                    flowText += `[Advanced rules for call]: ${node.data.extraCallInstructions}\n\n`;
                }
            }
            // Add chat suggestions prompt for chat conversations
            flowText += generateSuggestedRepliesPrompt(node.data.chatSuggestions, conversationType);
            // End of Instructions section
            if (script) {
                flowText += `**Script:**${TAB}
"${script}"\n\n`;
            }
            // Build options using sequential numbering
            if (outcomes.length > 0) {
                flowText += `- **Options:**\n`;
                const nodeOutcomeMapping = outcomeMapping.get(nodeId) || new Map();
                outcomes.forEach((outcome, index) => {
                    const targetNodeId = nodeOutcomeMapping.get(index);
                    if (targetNodeId) {
                        const sequentialNumber = `${nodeName}.${index + 1}`;
                        const targetNodeName = nodeNames.get(targetNodeId);
                        let optionText = `${TAB}- **${sequentialNumber}** ${outcome}`;
                        // If target has a different canonical number (convergence), add proceed notation with full label
                        if (targetNodeName && targetNodeName !== sequentialNumber) {
                            const targetParentOutcomes = parentOutcomes.get(targetNodeId) || [];
                            const uniqueTargetOutcomes = [...new Set(targetParentOutcomes)];
                            const targetLabel = uniqueTargetOutcomes.join(' / ');
                            optionText += ` (proceed to '### (${targetNodeName}) ${targetLabel}')`;
                        }
                        flowText += optionText + '\n';
                    }
                });
                flowText += '\n';
            }
            if (notes) {
                flowText += `**Notes:**${TAB}
${notes}\n\n`;
            }
            flowText += '\n';
            // Recursively process child nodes
            const outgoingEdges = edges.filter((edge) => edge.source === nodeId);
            for (const edge of outgoingEdges) {
                flowText += buildNodeContent(edge.target);
            }
            return flowText;
        };
        // Root nodes (nodes without incoming edges)
        const rootNodes = nodes.filter((node) => edges.every((edge) => edge.target !== node.id));
        let conversationFlow = '## Conversation Flow:\n\n';
        for (const rootNode of rootNodes) {
            conversationFlow += buildNodeContent(rootNode.id);
        }
        return conversationFlow;
    };
    // Execute steps
    const outcomeMapping = createOutcomeMapping();
    const nodeNames = assignNodeNames();
    const parentOutcomes = collectParentOutcomes();
    return generateContent(nodeNames, outcomeMapping, parentOutcomes, intakeFormBuilder);
};
export const conversationFlowPrompt_deprecated = (nodes, edges, conversationType, intakeFormConfig) => {
    // Helper function to recursively assign graph hierarchy numbers (1.X.Y.Z)
    const assignHierarchy = (nodeId, graphNumber = '1', parentOutcome, visited = new Set()) => {
        if (visited.has(nodeId)) {
            return ''; // Avoid cycles
        }
        visited.add(nodeId);
        const node = nodes.find((n) => n.id === nodeId);
        if (!node) {
            return ''; // Skip if node not found
        }
        const { instructions, script, notes, outcomes } = node.data;
        const sectionTitle = `(${graphNumber}) ${parentOutcome}`;
        // In MD, tabs are 2 spaces
        const TAB = '  ';
        // Generate the text for this node
        let flowText = `### ${sectionTitle}\n`;
        // Instructions section
        // conversationType = all is only for book keeping purposes, will never be used in live agents
        if (instructions) {
            flowText += `**Instructions:**${TAB}
${instructions}\n`;
        }
        // Inject intake form block instructions for intake nodes
        if (node.type === 'intakeNode' && intakeFormConfig?.blocks?.length) {
            const intakePrompt = generateIntakeFormPrompt(intakeFormConfig);
            flowText += `\n${intakePrompt}\n`;
        }
        if (node.data.extraChatInstructions) {
            if (conversationType === 'chat') {
                flowText += `${node.data.extraChatInstructions}\n`;
            }
            else if (conversationType === 'all') {
                flowText += `[Chat Instructions]: ${node.data.extraChatInstructions}\n\n`;
            }
        }
        if (node.data.extraCallInstructions) {
            if (conversationType === 'call') {
                flowText += `${node.data.extraCallInstructions}\n`;
            }
            else if (conversationType === 'all') {
                flowText += `[Call Instructions]: ${node.data.extraCallInstructions}\n\n`;
            }
        }
        // Add chat suggestions prompt for chat conversations
        flowText += generateSuggestedRepliesPrompt(node.data.chatSuggestions, conversationType);
        // End of Instructions section
        if (script) {
            flowText += `**Script:**${TAB}
"${script}"\n\n`;
        }
        // Build options from the outcomes
        if (outcomes.length > 0) {
            flowText += `- **Options:**\n`;
            outcomes.forEach((outcome, index) => {
                const optionNumber = `${graphNumber}.${index + 1}`;
                flowText += `${TAB}- **${optionNumber}** ${outcome}\n`;
            });
            flowText += '\n';
        }
        if (notes) {
            flowText += `**Notes:**${TAB}
${notes}\n\n`;
        }
        flowText += '\n';
        // Find outgoing edges from this node
        const outgoingEdges = edges.filter((edge) => edge.source === nodeId);
        for (const edge of outgoingEdges) {
            const sourceOutcomeIndex = parseInt(edge.sourceHandle?.replace(OUTCOME_HANDLE_ID_PREFIX, '') || '-1');
            const targetNode = nodes.find((n) => n.id === edge.target);
            if (targetNode && sourceOutcomeIndex !== -1) {
                const childGraphNumber = `${graphNumber}.${sourceOutcomeIndex + 1}`;
                const outcome = outcomes[sourceOutcomeIndex] || '';
                flowText += assignHierarchy(edge.target, childGraphNumber, outcome, visited);
            }
        }
        return flowText;
    };
    // Root nodes (nodes without incoming edges)
    const rootNodes = nodes.filter((node) => edges.every((edge) => edge.target !== node.id));
    let conversationFlow = '## Conversation Flow:\n\n';
    for (const rootNode of rootNodes) {
        conversationFlow += assignHierarchy(rootNode.id, `1`, rootNode.data.label);
    }
    return conversationFlow;
};
// The flat fields are the inbound opener; `outboundConfig`, when set, overrides
// them for outbound-only paths.
const resolveStartConfig = (startNodeData, direction) => {
    if (!startNodeData)
        return undefined;
    if (direction === 'outbound' && startNodeData.outboundConfig)
        return startNodeData.outboundConfig;
    return {
        script: startNodeData.script || '',
        firstMessageMode: startNodeData.firstMessageMode
    };
};
export const getFirstMessage = (promptConfig, conversationType = 'chat', direction = 'inbound') => {
    const startNode = promptConfig?.conversationFlow?.nodes.find((node) => node.type === 'startNode');
    if (!startNode)
        return '';
    const startNodeData = startNode.data;
    const resolved = resolveStartConfig(startNodeData, direction);
    const script = resolved?.script || '';
    // Suggested replies are chat-only, and chat is always inbound.
    const includeChatSuggestions = direction === 'inbound' && (conversationType === 'chat' || conversationType === 'all');
    if (!includeChatSuggestions)
        return script;
    return script + generateSuggestedRepliesXml(startNodeData.chatSuggestions);
};
export const getFirstMessageMode = (promptConfig, direction = 'inbound') => {
    const startNode = promptConfig?.conversationFlow?.nodes.find((node) => node.type === 'startNode');
    const resolved = resolveStartConfig(startNode?.data, direction);
    return resolved?.firstMessageMode;
};
// Returns both inbound and outbound openers concatenated. Used when scanning
// the prompt for required variables / actions, so direction-specific tokens
// (e.g. a `{{customer_name}}` only present in the outbound opener) aren't missed.
const getAllFirstMessages = (promptConfig, conversationType) => {
    const inbound = getFirstMessage(promptConfig, conversationType, 'inbound');
    const outbound = getFirstMessage(promptConfig, conversationType, 'outbound');
    return inbound === outbound ? inbound : `${inbound}\n${outbound}`;
};
export const getAgentTransferToolId = (promptConfig) => {
    return promptConfig?.conversationFlow?.nodes.find((node) => node.type === 'handoffNode')?.data?.transferToolId;
};
export const getAgentHandoffToolIds = (promptConfig) => {
    return promptConfig?.conversationFlow?.nodes
        .filter((node) => node.type === 'handoffNode')
        .map((node) => node.data.handoffToolId)
        .filter(Boolean);
};
export const getIntakeToolIds = (promptConfig) => {
    const intakeFormBuilder = promptConfig?.intakeFormBuilder;
    return [
        ...(intakeFormBuilder?.blocks ?? []).map((block) => block.vapiToolId),
        intakeFormBuilder?.validatorVapiToolId
    ].filter((toolId) => !!toolId);
};
export const getAgentActions = (promptConfig, conversationType) => {
    const ACTION_REGEX = /`(\w+)`/g;
    const promptText = `${getAllFirstMessages(promptConfig, conversationType)}\n${generateSystemPrompt(promptConfig, conversationType)}`;
    const actions = [];
    let match;
    while ((match = ACTION_REGEX.exec(promptText)) !== null) {
        const action = match[1];
        if (action && !actions.includes(action)) {
            actions.push(action);
        }
    }
    return actions;
};
export const generateSystemPrompt = (promptConfig, conversationType = 'call') => {
    // Handle channel-specific advanced prompts
    const getAdvancedPrompt = () => {
        let advancedPromptText = promptConfig.advancedPrompt || '';
        if (conversationType === 'chat') {
            if (promptConfig.advancedPromptChatOnly) {
                advancedPromptText += `\n${promptConfig.advancedPromptChatOnly}`;
            }
        }
        else if (conversationType === 'call') {
            if (promptConfig.advancedPromptCallOnly) {
                advancedPromptText += `\n${promptConfig.advancedPromptCallOnly}`;
            }
        }
        else {
            // conversationType === 'all'
            if (promptConfig.advancedPromptChatOnly) {
                advancedPromptText += `\n\n[Chat Advanced Prompt]:\n${promptConfig.advancedPromptChatOnly}`;
            }
            if (promptConfig.advancedPromptCallOnly) {
                advancedPromptText += `\n\n[Call Advanced Prompt]:\n${promptConfig.advancedPromptCallOnly}`;
            }
        }
        return advancedPromptText;
    };
    if (promptConfig.onlyUseAdvancedPrompt) {
        return getAdvancedPrompt();
    }
    const rolePrompt = `\n## Role:
${promptConfig.role}\n\n`;
    const objectivePrompt = `\n## Objective:
${promptConfig.objective}\n\nThe conversation flow is structured as a Directed Acyclic Graph (DAG) to ensure efficiency and reuse of components where appropriate. Follow the structure exactly, but your responses should always sound natural and conversational.\n`;
    const rules = `\n## Rules for the Chatbot:
1. Always follow the structured flow and reuse components (nodes) where indicated.
2. Respond naturally and conversationally without explicitly mentioning the node numbers or structure.`;
    const conversationFlow = conversationFlowPrompt(promptConfig.conversationFlow.nodes, promptConfig.conversationFlow.edges, conversationType, promptConfig.useNewPromptGenerator, promptConfig.intakeFormBuilder);
    const advancedPrompt = getAdvancedPrompt();
    return `${rolePrompt}\n\n---\n\n${objectivePrompt}\n\n---\n\n${conversationFlow}\n\n---\n\n${rules}\n\n---\n\n${advancedPrompt}\n\n`;
};
const liquidEngine = new Liquid();
/**
 * Variables a prompt can use with nothing passed in. The voice platform fills the caller's number from the phone call
 * and the time ones on every conversation, and Strada adds the last two on outbound calls.
 */
export const BUILT_IN_AGENT_VARIABLES = [
    'customer.number',
    'now',
    'date',
    'time',
    'month',
    'day',
    'year',
    STRADA_CURRENT_TIME,
    STRADA_CONTACT_TIMEZONE
];
/** Whether a variable is filled without being passed in. A phone call fills everything under `customer.`. */
export const isBuiltInAgentVariable = (variable) => BUILT_IN_AGENT_VARIABLES.includes(variable) || variable.startsWith('customer.');
/**
 * Returns user-defined variables from the prompt config.
 * These are variables explicitly defined by users in the Variables section of the prompt builder.
 * Use this when suggesting available variables for actions and configurations.
 * Also merges in required variables from the prompt text to keep backward compatibility
 * for agents that don't yet have an explicit variable list defined.
 */
export const getAgentVariables = (promptConfig, conversationType = 'all') => {
    if (!promptConfig)
        return [];
    const userDefinedVariables = (promptConfig.variables || []).filter((variable) => ![STRADA_CONTACT_TIMEZONE, STRADA_CURRENT_TIME].includes(variable));
    const requiredVariables = getRequiredAgentVariables(promptConfig, conversationType);
    const allVariables = Array.from(new Set([...userDefinedVariables, ...requiredVariables]));
    return allVariables;
};
/**
 * Returns variables that are actually referenced in the prompt using {{variable}} syntax.
 * These are "required" variables that must be provided when running campaigns.
 * Use this for campaign validation and required field checks.
 */
export const getRequiredAgentVariables = (promptConfig, conversationType = 'all') => {
    try {
        const promptText = `${getAllFirstMessages(promptConfig, conversationType)}\n${generateSystemPrompt(promptConfig, conversationType)}`;
        const liquidVariables = liquidEngine.fullVariablesSync(promptText);
        const filteredVariables = liquidVariables.filter((variable) => ![STRADA_CONTACT_TIMEZONE, STRADA_CURRENT_TIME].includes(variable));
        return filteredVariables;
    }
    catch {
        return [];
    }
};
//# sourceMappingURL=generate-system-prompt.js.map